import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { AiOutlineClose } from 'react-icons/ai';
import CryptoJS from 'crypto-js';
import { toast } from 'react-toastify';
import '../scss/send-invoice-confirmation-modal.scss';
import { InvoiceSuccessModal } from './invoice-success-modal.component';
import { formatAmountWithLocale } from '../helper/common.helper';
import { Helmet } from 'react-helmet';

// Extend Window interface for Google Tag Manager dataLayer
declare global {
  interface Window {
    dataLayer: any[];
  }
}

interface FreelancerSubtotal {
  name: string;
  subtotal: string;
}

interface SendInvoiceConfirmationModalProps {
  show: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<any>;
  onSuccess?: () => void;
  isLoading?: boolean;
  isEditMode?: boolean;
  invoiceAmount: string;
  invoiceDay: string;
  dueDay: string;
  freelancerSubtotals?: FreelancerSubtotal[];
  calculatedSubAmount?: any;
  selectedCustomer?: any;
  customerIdFromQuery?: number;
  preId?: string;
  dueDateFromEdit?: string; // NEW - dueDate from edit mode
}

export const SendInvoiceConfirmationModal: React.FC<SendInvoiceConfirmationModalProps> = ({
  show,
  onClose,
  onConfirm,
  onSuccess,
  isLoading = false,
  isEditMode = false,
  invoiceAmount,
  invoiceDay,
  dueDay,
  freelancerSubtotals = [],
  calculatedSubAmount,
  selectedCustomer,
  customerIdFromQuery,
  preId,
  dueDateFromEdit,
}) => {
  const { t, i18n } = useTranslation();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [wasSuccessful, setWasSuccessful] = useState(false);
  
  // 🆕 GTM Tracking State
  const [isAlreadySynced, setIsAlreadySynced] = useState<boolean>(true);
  
  // 🆕 Get user email from Redux
  const userEmail = useSelector((state: any) => state?.auth?.user);
  const userId = useSelector((state:any) => state.auth.user?.userId)
  
  // Handler for confirm button
  const handleConfirm = async () => {
    try {
      const result = await onConfirm();
     
      // Check for 500 status code error
      if (result && typeof result === 'object' && result.statusCode === 500) {
        // Specific Fortnox "invalid social security number" error -> translated message
        if (/Ej giltigt personnummer/i.test(result.message || '')) {
          toast.error(String(t('INVALID_SOCIAL_SECURITY_NUMBER')));
        } else {
          toast.error(String(t('INVOICE_SAVE_ERROR_500')));
        }
        // Don't proceed to success modal, stay on confirmation modal
        return;
      }

      // Check for non-200/201 status codes
      if (result && typeof result === 'object' && result.statusCode && 
          result.statusCode !== 200 && result.statusCode !== 201) {
        // Show error toast for other error status codes
        const errorMessage = result.message || String(t('INVOICE_SAVE_ERROR_500'));
        toast.error(errorMessage);
        // Don't proceed to success modal
        return;
      }

      // 🆕 Extract isAlreadySynced from API response
      if (result && typeof result === 'object') {
        // Check standard API response path: response.isAlreadySynced
        const syncStatus = result?.response?.isAlreadySynced;
        
        if (syncStatus !== undefined && syncStatus !== null) {
          // Got isAlreadySynced from API response - use it directly
          setIsAlreadySynced(syncStatus);
        }
      }
      
      // If the API call was successful (statusCode 200 or 201), show success modal
      // The result will be true or a successful response
      if (result !== false) {
        setWasSuccessful(true);
        setShowSuccessModal(true);
      }
    } catch (error) {
      // Error is already handled by the parent component
      console.error('Error in handleConfirm:', error);
    }
  };
  
  // Handler for success modal close
  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    // Call onSuccess callback if provided and operation was successful
    if (wasSuccessful && onSuccess) {
      onSuccess();
    }
    onClose(); // Also close the confirmation modal
  };
  
  // Get createdDate from URL parameters
  const getCreatedDateFromUrl = (): string | null => {
    const searchParams = new URLSearchParams(window.location.search);
    const createdDate = searchParams.get('createdDate');
    if (createdDate) {
      // Parse ISO date and format as DD-MM-YYYY
      try {
        const date = new Date(createdDate);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${day}-${month}-${year}`;
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  // Format date from ISO or YYYY-MM-DD to DD-MM-YYYY
  const formatDateToDDMMYYYY = (dateString: string | undefined | null): string | null => {
    if (!dateString) return null;
    
    try {
      // Check if already in DD-MM-YYYY format
      if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
        return dateString;
      }
      
      // Parse date (handles both ISO and YYYY-MM-DD formats)
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return null;
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${day}-${month}-${year}`;
    } catch (e) {
     return null;
    }
  };

  // Get current date in DD-MM-YYYY format
  const getCurrentDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${day}-${month}-${year}`;
  };
  
  // Calculate due date based on current date + paymentTerm
  const calculateDueDate = () => {
    // Convert paymentTerm string to number, default to 30 if not available
    const paymentTerm = selectedCustomer?.paymentTerm ? parseInt(selectedCustomer.paymentTerm, 10) : 30;
    const today = new Date();
    const dueDate = new Date(today);
    dueDate.setDate(dueDate.getDate() + paymentTerm);
    
    const year = dueDate.getFullYear();
    const month = String(dueDate.getMonth() + 1).padStart(2, '0');
    const day = String(dueDate.getDate()).padStart(2, '0');
    return `${day}-${month}-${year}`;
  };
  
  // Use createdDate from URL if available, otherwise use invoiceDay prop or current date
  const displayInvoiceDay = React.useMemo(() => {
    const createdDateFromUrl = getCreatedDateFromUrl();
    return createdDateFromUrl || invoiceDay || getCurrentDate();
  }, [invoiceDay]);
  
  // Use dueDateFromEdit prop if available (format it properly), otherwise use dueDay prop or calculated due date
  const displayDueDay = React.useMemo(() => {
    const formattedDueDateFromEdit = formatDateToDDMMYYYY(dueDateFromEdit);
    return formattedDueDateFromEdit || dueDay || calculateDueDate();
  }, [dueDateFromEdit, dueDay, selectedCustomer?.paymentTerm]);
  
 
  // Log calculatedSubAmount for debugging
  // Extract freelancer subtotals from calculatedSubAmount
  const extractedFreelancerSubtotals: FreelancerSubtotal[] = React.useMemo(() => {
    if (calculatedSubAmount?.response?.freelancersData && Array.isArray(calculatedSubAmount.response.freelancersData)) {
      return calculatedSubAmount.response.freelancersData.map((freelancer: any) => ({
        name: freelancer.freelancerName || 'Unknown',
        subtotal: formatAmountWithLocale(freelancer.amount || 0, freelancer.currencyName || 'SEK', i18n.language)
      }));
    }
    return freelancerSubtotals || [];
  }, [calculatedSubAmount, freelancerSubtotals]);

  // Extract invoice amount from calculatedSubAmount
  const displayInvoiceAmount = React.useMemo(() => {
    if (calculatedSubAmount?.response?.totalCalculation?.amountIncludingVat) {
      const amount = calculatedSubAmount.response.totalCalculation.amountIncludingVat;
      const currency = calculatedSubAmount.response.totalCalculation.currencyName || 'SEK';
      return formatAmountWithLocale(amount, currency, i18n.language);
    }
    
    // Parse invoiceAmount string to extract amount and currency
    if (invoiceAmount) {
      const match = invoiceAmount.match(/([\d,.]+)\s*([A-Z]+)/);
      if (match) {
        const amount = parseFloat(match[1].replace(/,/g, ''));
        const currency = match[2];
        return formatAmountWithLocale(amount, currency, i18n.language);
      }
    }
    
    return invoiceAmount;
  }, [calculatedSubAmount, invoiceAmount, i18n.language]);
  useEffect(() => {
    if (!isAlreadySynced && userEmail.email) {
      const hashedEmail = CryptoJS.SHA256(userEmail.email.trim().toLowerCase()).toString(CryptoJS.enc.Hex);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "send_invoice",
        email: hashedEmail,
      });
    }
  }, [isAlreadySynced, userEmail.email]);
  return (
    <>
      {!isAlreadySynced && (
        <Helmet>
          <script type="text/javascript">
            {`
                  if (typeof tdconv !== "undefined" && tdconv !== null) {
                    tdconv('init', '2358708', {'element': 'iframe'});
                    tdconv('track', 'sale', {'transactionId': '${userId}', 'ordervalue': '0', 'currency': 'SEK', 'event': 437992});
                  }
                `}
          </script>
        </Helmet>
      )}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .send-invoice-btn {
          -webkit-tap-highlight-color: transparent !important;
          -webkit-touch-callout: none !important;
          -webkit-user-select: none !important;
          user-select: none !important;
          touch-action: manipulation !important;
        }
        
        .send-invoice-btn:active {
          transform: scale(0.98);
          transition: transform 0.1s ease;
        }
        
        .send-invoice-btn svg {
          will-change: transform;
          -webkit-backface-visibility: hidden;
          backface-visibility: hidden;
          -webkit-transform: translateZ(0);
          transform: translateZ(0);
        }
      `}</style>
      <Modal 
        show={show && !showSuccessModal} 
        onHide={onClose} 
        centered 
        className="send-invoice-confirmation-modal"
      >
      <Modal.Header>
        <Modal.Title>
          {isEditMode ? t('EDIT_REQUEST_INVOICE') : t('CONFIRM_SENDING_INVOICE')}
        </Modal.Title>
        <AiOutlineClose
          className="close-icon"
          size={24}
          onClick={onClose}
        />
      </Modal.Header>

      <Modal.Body>
        <h5 className="confirmation-question">
          {t('ARE_YOU_SURE_SEND_INVOICE')}
        </h5>

        {/* Collective Mount Section - Only show if there are freelancer subtotals */}
        {extractedFreelancerSubtotals && extractedFreelancerSubtotals.length > 0 && (
          <div className="collective-mount-section">
            <p className="section-label">{t('COLLECTIVE_MOUNT')}</p>
            {extractedFreelancerSubtotals.map((freelancer, index) => (
              <div key={index} className="freelancer-subtotal-row">
                <span className="freelancer-name">{freelancer.name}</span>
                <span className="freelancer-subtotal">
                  {t('SUB_TOTAL')}: {freelancer.subtotal}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Invoice Amount Section */}
        <div className="invoice-amount-section">
          <div className="invoice-amount-box">
            <p className="amount-label">{t('INVOICE_AMOUNT')}</p>
            <h2 className="amount-value">{displayInvoiceAmount}</h2>
            
            <div className="invoice-dates">
              <div className="date-row">
                <span className="date-label">{t('INVOICE_DAY')}</span>
                <span className="date-value">{displayInvoiceDay}</span>
              </div>
              <div className="date-row">
                <span className="date-label">{t('DUE_DAY')}</span> 
                <span className="date-value">{displayDueDay}</span>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>

      
      {!selectedCustomer?.hasAnyInvoice && !customerIdFromQuery && !isEditMode && !preId && (
        <div className="warning-message-container">
          <div className="alert alert-warning" role="alert">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="warning-icon">
              <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM10 11C9.45 11 9 10.55 9 10V6C9 5.45 9.45 5 10 5C10.55 5 11 5.45 11 6V10C11 10.55 10.55 11 10 11ZM11 15H9V13H11V15Z" fill="currentColor"/>
            </svg>
            <span>{t('INVOICE_WARNING_MESSAGE')}</span>
          </div>
        </div>
      )} 

      <Modal.Footer>
        <button
          type="button"
          className="cancel-btn"
          onClick={onClose}
          disabled={isLoading}
        >
          {t('CANCEL')}
        </button>
        <button
          type="button"
          className="send-invoice-btn"
          onClick={handleConfirm}
          disabled={isLoading}
          style={{
            WebkitTapHighlightColor: 'transparent',
            WebkitTouchCallout: 'none',
            WebkitUserSelect: 'none',
            touchAction: 'manipulation'
          }}
        >
          {isLoading ? (
            <>
              <svg 
                width="16" 
                height="16" 
                viewBox="0 0 16 16" 
                fill="none" 
                style={{ 
                  animation: 'spin 1s linear infinite',
                  display: 'inline-block',
                  verticalAlign: 'middle',
                  marginRight: '8px'
                }}
              >
                <circle cx="8" cy="8" r="6" stroke="white" strokeWidth="2" strokeDasharray="30" strokeLinecap="round" opacity="0.3"/>
                <circle cx="8" cy="8" r="6" stroke="white" strokeWidth="2" strokeDasharray="10 20" strokeLinecap="round"/>
              </svg>
              <span>{t('SENDING')}</span>
            </>
          ) : (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ marginRight: '8px', display: 'inline-block', verticalAlign: 'middle' }}>
                <path d="M15.0013 3.00182L7.28117 10.1177C7.20414 10.1887 7.16032 10.2887 7.16032 10.3934V14.7381C7.16032 15.0862 7.59347 15.2465 7.82004 14.9821L10.8421 11.4564M3.12142 8.56672L15.1903 2.53231C15.4439 2.40547 15.7411 2.59526 15.7328 2.87874L15.4585 12.2047C15.4513 12.4482 15.2175 12.6203 14.9829 12.5548L3.18833 9.26333C2.85782 9.1711 2.81451 8.72017 3.12142 8.56672Z" stroke="white" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <span>{isEditMode ? t('EDIT_REQUEST_INVOICE') : t('SEND_INVOICE')}</span>
            </>
          )}
        </button>
      </Modal.Footer>
    </Modal>
    
    {/* Success Modal */}
    <InvoiceSuccessModal
      show={showSuccessModal}
      onClose={handleSuccessClose}
      isEditMode={isEditMode}
    />
  </>
  );
};

export default SendInvoiceConfirmationModal;
